import { CPLAT_API_ROUTES, useFetch } from '@/api';
import {
  ArchiveOutline,
  CarOutline,
  CheckmarkCircleOutline,
  CloseCircleOutline,
  CornerDownLeftOutline,
  ShoppingBagOutline,
  SwapOutline,
} from '@/assets/icons/Dashboard';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/molecules/DropdownMenu';
import { useAppSelector } from '@/redux/store';
import { CplatApiResponse } from '@/types/api';
import { cn } from '@/utils/tailwind';
import { ChevronDownIcon } from 'lucide-react';
import moment from 'moment';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardCard } from '../card';
import { ROUTES } from '@/routes';

const OrderFilter = [
  { id: 0, label: '오늘', value: 'today' },
  { id: 1, label: '최근 7일', value: '7day' },
  { id: 2, label: '1개월', value: '1month' },
];

type TabType = 'new' | 'change';
type FilterType =
  | 'new-order'
  | 'ready-to-ship'
  | 'shipping'
  | 'delivered'
  | 'cancel'
  | 'exchange'
  | 'return';

interface StatusItem {
  label: string;
  count?: number;
  tab: TabType;
  filter: FilterType;
  icon?: JSX.Element;
}

const OrderSituation = () => {
  const navigate = useNavigate();
  const user = useAppSelector((state) => state.user);
  const now = moment();

  // ✅ 선택된 필터 상태 관리
  const [selectedFilter, setSelectedFilter] = useState<'today' | '7day' | '1month'>('today');

  // ✅ 날짜 범위 계산
  const { dateFrom, dateTo } = useMemo(() => {
    const dateTo = now.format('YYYY-MM-DD');
    let dateFrom = now.clone();

    if (selectedFilter === '7day') {
      dateFrom = now.clone().subtract(6, 'days');
    } else if (selectedFilter === '1month') {
      dateFrom = now.clone().subtract(1, 'month');
    }

    return { dateFrom: dateFrom.format('YYYY-MM-DD'), dateTo };
  }, [selectedFilter]);

  // ✅ API 호출 (선택된 기간에 맞게 변경)
  const { data } = useFetch<
    CplatApiResponse<
      {
        statusDetails: {
          statusName: string;
          totalSalesAmount: number;
          orderCount: number;
        }[];
        grandTotalSales: number;
        grandTotalCount: number;
      }[]
    >
  >(CPLAT_API_ROUTES.getOrderStatusSummary, {
    method: 'POST',
    params: {
      cplatToken: user.CplatToken,
      dateFrom,
      dateTo,
    },
    v3: true,
    // ✅ 필터 변경 시 다시 요청
  });

  const statusDetails = data?.data?.[0]?.statusDetails ?? [];

  const getCount = (name: string) =>
    statusDetails.find((item) => item.statusName === name)?.orderCount ?? 0;

  const newOrders: StatusItem[] = [
    {
      label: '신규 주문',
      count: getCount('신규 주문'),
      tab: 'new',
      filter: 'new-order',
      icon: <ShoppingBagOutline />,
    },
    {
      label: '배송 준비',
      count: getCount('배송 준비'),
      tab: 'new',
      filter: 'ready-to-ship',
      icon: <ArchiveOutline />,
    },
    {
      label: '배송중',
      count: getCount('배송중'),
      tab: 'new',
      filter: 'shipping',
      icon: <CarOutline />,
    },
    {
      label: '배송완료',
      count: getCount('배송완료'),
      tab: 'new',
      filter: 'delivered',
      icon: <CheckmarkCircleOutline />,
    },
  ];

  const changedOrders: StatusItem[] = [
    {
      label: '취소요청',
      count: getCount('취소요청'),
      tab: 'change',
      filter: 'cancel',
      icon: <CloseCircleOutline />,
    },
    {
      label: '교환요청',
      count: getCount('교환요청'),
      tab: 'change',
      filter: 'exchange',
      icon: <CornerDownLeftOutline />,
    },
    {
      label: '반품요청',
      count: getCount('반품요청'),
      tab: 'change',
      filter: 'return',
      icon: <SwapOutline />,
    },
  ];

  const orders = [...newOrders, ...changedOrders];

  return (
    <DashboardCard>
      <div className="flex items-center justify-between">
        <span className="text-gr-900 Body1S16">주문 현황</span>

        {/* ✅ 필터 드롭다운 */}
        <DropdownMenu>
          <DropdownMenuTrigger className="flex items-center gap-1 focus:outline-none">
            <span className="text-gr-700 Body5M14">
              {OrderFilter.find((f) => f.value === selectedFilter)?.label}
            </span>
            <ChevronDownIcon className="h-4 w-4 text-gr-600" />
          </DropdownMenuTrigger>

          <DropdownMenuContent>
            {OrderFilter.map((filter) => (
              <DropdownMenuItem
                key={filter.value}
                onClick={() => setSelectedFilter(filter.value as 'today' | '7day' | '1month')}
              >
                {filter.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* ✅ 주문 현황 카드 */}
      <div className="grid h-[144px] grid-cols-4 grid-rows-2 gap-3">
        {orders.map((order) => (
          <button
            key={order.filter}
            className={cn('flex flex-col items-center justify-center rounded-[4px] bg-gr-50', {
              'row-span-full bg-point-100': order.filter === 'new-order',
            })}
            onClick={() => navigate(`${ROUTES.orderDetails}?orderTab=${order.filter}`)}
          >
            <div className="text-gr-500 Body5M14">{order.label}</div>
            <div
              className={cn({
                'text-point-500 Title2S24': order.filter === 'new-order',
                Title3S18: order.filter !== 'new-order',
              })}
            >
              {order.count?.toLocaleString()}
            </div>
          </button>
        ))}
      </div>
    </DashboardCard>
  );
};

export default OrderSituation;
